import { FC, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, Link as RouterLink, useParams } from 'react-router';
import { Box, Grid, IconButton, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, InfoOutline } from '@mui/icons-material';

import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';
import { PermissionedView } from '@cvt/components/Permissioned';
import { ListLoading } from '@cvt/components/layout/ListLoading';

import { SpecializationRow, SpecializationRowSkeleton } from '@modules/Specializations/components/SpecializationRow';
import { InfiniteSpecializationsList } from '@modules/Specializations/partials/InfiniteSpecializationsList';
import { useMajor } from '@modules/Majors/hooks/useMajor';

import { formatPercentage } from '@shared/helpers/number';
import { ViewHeader } from '@shared/components/ViewHeader';

import { ViewUniversityMajorSpecialization as ViewUniversityMajorSpecializationView } from './views/ViewUniversityMajorSpecialization';

interface ViewProps {
  university: Universities.University;
  major: Majors.Major;
}

export const ViewUniversityMajorView: FC<ViewProps> = ({ university, major }) => {
  const router = useRouter();
  const location = useLocation();

  return (
    <Stack direction="column">
      <Grid container spacing={2}>
        <Grid size={12}>
          <ViewHeader
            category={university.name}
            title={major.title}
            slots={{
              left: (
                <IconButton component={RouterLink} to={router.universities.view(university.id).majors.path} color="primary">
                  <ArrowBack/>
                </IconButton>
              ),
            }}
          />
          <Box px={2} mb={4}>
            <Stack direction="row" justifyContent="flex-end" alignItems="center">
              <Typography variant="body2">{formatPercentage(major.match.value)} Match</Typography>
              <IconButton component={RouterLink} to={router.majors.view(major.id).assertions.path} color="primary" size="small">
                <InfoOutline fontSize="small" />
              </IconButton>
            </Stack>
            <LinearProgress value={major.match.value * 100} variant="determinate" />
          </Box>
          <Box px={2} mb={3}>
            <Typography variant="body1">{major.description}</Typography>
          </Box>
          {/* SPECIALIZATIONS OFFERED */}
          <Box
            p={2}
            width="100%"
            sx={theme => ({ backgroundColor: theme.palette.tertiary.light })}
          >
            <Typography variant="subtitle1" mb={0.5}>Specializations</Typography>
            <ErrorBoundary>
              <PermissionedView permission="majors.list">
                <Paper variant="outlined">
                  <Suspense key={location.pathname} fallback={(
                    <ListLoading
                      disablePadding
                      repeat={3}
                      renderItem={() => <SpecializationRowSkeleton/>}
                    />
                  )}>
                    <InfiniteSpecializationsList
                      filters={{
                        university: university.id,
                        major: major.id,
                      }}
                      renderItem={specialization => <SpecializationRow key={major.id} {...specialization} linkTo={router.universities.view(university.id).majors.view(major.id).specializations.view(specialization.id).path} />}
                      renderSkeleton={() => <SpecializationRowSkeleton/>}
                    />
                  </Suspense>
                </Paper>
              </PermissionedView>
            </ErrorBoundary>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};


export const ViewUniversityMajorSpecialization: FC<{
  university: Universities.University;
  major: Majors.Major;
}> = ({
  university,
  major,
}) => {
  const { specializationId = '' } = useParams();

  return (
    <ViewUniversityMajorSpecializationView
      id={parseInt(specializationId, 10)}
      university={university}
      major={major}
    />
  );
};


interface Props {
  id: number;
  university: Universities.University;
}

export const ViewUniversityMajor: FC<Props> = ({ id, university }) => {

  const router = useRouter();

  const { major } = useMajor({
    id,
  });

  if (!major) {
    return null;
  }

  return (
    
    <Routes>
      <Route path="/specializations/:specializationId" element={<ViewUniversityMajorSpecialization university={university} major={major} />} />
      <Route path="/*" element={<ViewUniversityMajorView university={university} major={major} />}/>
      <Route path="*" element={<Navigate to={router.majors.view(major.id).path} replace />}/>
    </Routes>
  );
};
